import { useState } from 'react';
import { useReduxState } from 'hooks';
import { Expand } from 'components/common';
import { Icon } from 'components/common/icons';

import type { SupportedFormHelpTextServerConfigKeys } from 'shared-types';

interface Props {
  children: React.ReactNode;
  helpText?: string;
  helpTextServerConfigKey?: SupportedFormHelpTextServerConfigKeys;
  isExpandable?: boolean;
  isExpandableIconAfterTitle?: boolean;
  maxWidth?: string;
  nonExpandedContent?: React.ReactNode;
  style?: React.CSSProperties;
  title?: string | React.ReactNode;
  tooltip?: string;
}

const FormSection = ({
  children,
  helpText,
  helpTextServerConfigKey,
  isExpandable,
  isExpandableIconAfterTitle = false,
  maxWidth,
  nonExpandedContent,
  style = {},
  title,
  tooltip
}: Props) => {
  const {
    language,
    theme,
    serverConfig: { formHelpTextOptions }
  } = useReduxState();

  const [isExpanded, setIsExpanded] = useState(false);

  const ExpandableIcon = ({ inLine }: { inLine: boolean }) =>
    isExpandable ? (
      <div className={inLine ? 'expandable-icon-inline' : 'expandable-icon'}>
        <Icon
          icon={isExpanded ? 'chevron-up' : 'chevron-down'}
          fixedWidth={true}
        />

        <style jsx>{`
          .expandable-icon {
            color: ${theme.textColorLight};
            font-size: 1.4rem;
            position: absolute;
            top: 15px;
            right: 15px;
          }
          .expandable-icon-inline {
            display: inline-block;
            font-size: 1.2rem;
            margin-left: 10px;
          }
        `}</style>
      </div>
    ) : null;

  const globalHelpText =
    formHelpTextOptions?.[helpTextServerConfigKey]?.translations?.[language] ||
    formHelpTextOptions?.[helpTextServerConfigKey]?.value;

  return (
    <div
      className={`section ${isExpandable ? 'section-expandable' : ''} ${
        isExpanded ? 'section-expanded' : ''
      }`}
      style={style}
    >
      <div
        className="header"
        {...(isExpandable ? { onClick: () => setIsExpanded(!isExpanded) } : {})}
      >
        {title && tooltip ? (
          <div className="tooltip" data-tooltip={tooltip}>
            <div className="title">
              {title}

              <Icon
                icon="question-circle"
                style={{
                  fontSize: '1.2rem',
                  marginLeft: 5,
                  position: 'relative',
                  top: -1
                }}
              />

              {isExpandableIconAfterTitle ? (
                <ExpandableIcon inLine={true} />
              ) : null}
            </div>
          </div>
        ) : title ? (
          <div className="title">
            {title}

            {isExpandableIconAfterTitle ? (
              <ExpandableIcon inLine={true} />
            ) : null}
          </div>
        ) : null}

        {globalHelpText || helpText ? (
          <div className="help-text">{globalHelpText || helpText}</div>
        ) : null}

        {!isExpandableIconAfterTitle ? <ExpandableIcon inLine={false} /> : null}
      </div>

      {isExpandable && !isExpanded ? (
        <div className="non-expand-content">{nonExpandedContent}</div>
      ) : null}

      {isExpandable ? (
        <Expand isExpanded={isExpanded}>
          <div className="content">{children}</div>
        </Expand>
      ) : (
        <div className="content">{children}</div>
      )}

      <style jsx>{`
        .section {
          padding: 15px;
          position: relative;
        }
        .section:not(:first-child) {
          border-top: 1px solid ${theme.lightGrey};
          margin-top: 25px;
        }
        .section-expandable:not(:first-child) {
          margin-top: 0;
        }
        .section-expanded {
          margin-bottom: 25px;
        }
        .section-expandable .header {
          cursor: pointer;
        }
        .tooltip {
          display: inline-block;
        }
        .title {
          color: ${theme.textColorLight};
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          padding: 0;
          margin: 0 0 15px;
        }
        .help-text {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: normal;
          margin: -12px 0 15px;
        }
        .non-expand-content {
          margin-top: 15px;
        }
        .content {
          max-width: ${maxWidth || '540px'};
        }
        .section-expandable .title {
          margin: 0;
        }
        .section-expandable .help-text {
          margin: 3px 0 15px;
        }
        .section-expandable .content {
          margin-top: 15px;
        }
      `}</style>
    </div>
  );
};

export default FormSection;
